import { cn } from "@/util/cn";
import type { ISourceOptions } from "@tsparticles/engine";
import Particles from "@tsparticles/react";
import { motion } from "motion/react";
import { useSpotlightEffect } from "./use-spotlight-effect";

export function ParticlesPattern({ type }: { type: "sparkle" | "fireworks" }) {
    const { style, onMouseMove, onMouseLeave, setRef, isVisible } = useSpotlightEffect();

    return (
        <div className="z-10 h-full w-full" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} ref={setRef}>
            {isVisible && (
                <motion.div
                    className="h-full w-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 100 }}
                    transition={{ ease: "easeOut", duration: 1 }}
                >
                    <motion.div
                        className={cn(
                            "absolute inset-0 bg-linear-to-r from-accent-flashy to-purple backdrop-blur-xl transition duration-500",
                            {
                                "to-violet-700": type === "sparkle",
                                "to-indigo-600": type === "fireworks",
                            },
                        )}
                        style={style}
                    />

                    <motion.div className="h-full w-full" style={style}>
                        <ParticelComponent type={type} />
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
}

function ParticelComponent({ type }: { type: "sparkle" | "fireworks" }) {
    return (
        <div className="relative h-full w-full">
            {type === "sparkle" ? (
                <Particles
                    id="sparkle"
                    particlesLoaded={async () => {}}
                    options={sparkleOptions}
                    className="h-full w-full"
                />
            ) : (
                <Particles id="fireworks" particlesLoaded={async () => {}} options={test} className="h-full w-full" />
            )}
        </div>
    );
}

const sparkleOptions: ISourceOptions = {
    fullScreen: {
        enable: false,
        zIndex: 0,
    },
    fpsLimit: 120,
    interactivity: {
        events: {
            onHover: {
                enable: true,
                mode: "attract",
            },
        },
        modes: {
            attract: {
                distance: 60,
                duration: 1,
                speed: 0.3,
            },
        },
    },
    particles: {
        bounce: {
            horizontal: {
                value: 1,
            },
            vertical: {
                value: 1,
            },
        },
        collisions: {
            absorb: {
                speed: 2,
            },
            bounce: {
                horizontal: {
                    value: 1,
                },
                vertical: {
                    value: 1,
                },
            },
            enable: false,
            maxSpeed: 50,
            mode: "bounce",
            overlap: {
                enable: true,
                retries: 0,
            },
        },
        color: {
            value: "#ffffff",
            animation: {
                h: {
                    count: 0,
                    enable: false,
                    speed: 1,
                    decay: 0,
                    delay: 0,
                    sync: true,
                    offset: 0,
                },
                s: {
                    count: 0,
                    enable: false,
                    speed: 1,
                    decay: 0,
                    delay: 0,
                    sync: true,
                    offset: 0,
                },
                l: {
                    count: 0,
                    enable: false,
                    speed: 1,
                    decay: 0,
                    delay: 0,
                    sync: true,
                    offset: 0,
                },
            },
        },
        effect: {
            close: true,
            fill: true,
            options: {},
            type: undefined,
        },
        groups: {},
        move: {
            angle: {
                offset: 0,
                value: 90,
            },
            attract: {
                distance: 200,
                enable: false,
                rotate: {
                    x: 3000,
                    y: 3000,
                },
            },
            center: {
                x: 50,
                y: 50,
                mode: "percent",
                radius: 0,
            },
            decay: 0,
            distance: {},
            direction: "none",
            drift: 0,
            enable: true,
            gravity: {
                acceleration: 9.81,
                enable: false,
                inverse: false,
                maxSpeed: 50,
            },
            path: {
                clamp: true,
                delay: {
                    value: 0,
                },
                enable: false,
                options: {},
            },
            outModes: {
                default: "out",
            },
            random: false,
            size: false,
            speed: {
                min: 0.1,
                max: 1,
            },
            spin: {
                acceleration: 0,
                enable: false,
            },
            straight: false,
            trail: {
                enable: false,
                length: 10,
                fill: {},
            },
            vibrate: false,
            warp: false,
        },
        number: {
            density: {
                enable: true,
                width: 400,
                height: 400,
            },
            limit: {
                mode: "delete",
                value: 0,
            },
            value: 1200,
        },
        opacity: {
            value: {
                min: 0.1,
                max: 1,
            },
            animation: {
                count: 0,
                enable: true,
                speed: 4,
                decay: 0,
                delay: 0,
                sync: false,
                mode: "auto",
                startValue: "random",
                destroy: "none",
            },
        },
        reduceDuplicates: false,
        shadow: {
            blur: 0,
            color: {
                value: "#000",
            },
            enable: false,
            offset: {
                x: 0,
                y: 0,
            },
        },
        shape: {
            close: true,
            fill: true,
            options: {},
            type: "circle",
        },
        size: {
            value: {
                min: 0.7,
                max: 1.5,
            },
            animation: {
                count: 0,
                enable: false,
                speed: 5,
                decay: 0,
                delay: 0,
                sync: false,
                mode: "auto",
                startValue: "random",
                destroy: "none",
            },
        },
        stroke: {
            width: 0,
        },
        zIndex: {
            value: 0,
            opacityRate: 1,
            sizeRate: 1,
            velocityRate: 1,
        },
        destroy: {
            bounds: {},
            mode: "none",
            split: {
                count: 1,
                factor: {
                    value: 3,
                },
                rate: {
                    value: {
                        min: 4,
                        max: 9,
                    },
                },
                sizeOffset: true,
            },
        },
        roll: {
            darken: {
                enable: false,
                value: 0,
            },
            enable: false,
            enlighten: {
                enable: false,
                value: 0,
            },
            mode: "vertical",
            speed: 25,
        },
        tilt: {
            value: 0,
            animation: {
                enable: false,
                speed: 0,
                decay: 0,
                sync: false,
            },
            direction: "clockwise",
            enable: false,
        },
        twinkle: {
            lines: {
                enable: false,
                frequency: 0.05,
                opacity: 1,
            },
            particles: {
                enable: false,
                frequency: 0.05,
                opacity: 1,
            },
        },
        wobble: {
            distance: 5,
            enable: false,
            speed: {
                angle: 50,
                move: 10,
            },
        },
        life: {
            count: 0,
            delay: {
                value: 0,
                sync: false,
            },
            duration: {
                value: 0,
                sync: false,
            },
        },
        rotate: {
            value: 0,
            animation: {
                enable: false,
                speed: 0,
                decay: 0,
                sync: false,
            },
            direction: "clockwise",
            path: false,
        },
        orbit: {
            animation: {
                count: 0,
                enable: false,
                speed: 1,
                decay: 0,
                delay: 0,
                sync: false,
            },
            enable: false,
            opacity: 1,
            rotation: {
                value: 45,
            },
            width: 1,
        },
        links: {
            blink: false,
            color: {
                value: "#fff",
            },
            consent: false,
            distance: 100,
            enable: false,
            frequency: 1,
            opacity: 1,
            shadow: {
                blur: 5,
                color: {
                    value: "#000",
                },
                enable: false,
            },
            triangles: {
                enable: false,
                frequency: 1,
            },
            width: 1,
            warp: false,
        },
        repulse: {
            value: 0,
            enabled: false,
            distance: 1,
            duration: 1,
            factor: 1,
            speed: 1,
        },
    },
    detectRetina: true,
};

const test: ISourceOptions = {
    fullScreen: {
        enable: false,
        zIndex: 0,
    },
    fpsLimit: 120,
    interactivity: {
        events: {
            onHover: {
                enable: true,
                mode: "attract",
            },
        },
    },
    particles: {
        color: {
            value: "#ffffff",
        },
        lineLinked: {
            color: "#ffffff",
            distance: 150,
            enable: true,
            opacity: 0.4,
            width: 1,
        },
        links: {
            blink: false,
            color: {
                value: "#fff",
            },
            consent: false,
            distance: 70,
            enable: true,
            frequency: 1,
            opacity: 1,
            shadow: {
                blur: 5,
                color: {
                    value: "#000",
                },
                enable: false,
            },
            triangles: {
                enable: false,
                frequency: 1,
            },
            width: 1,
            warp: false,
        },
        move: {
            angle: {
                offset: 0,
                value: 90,
            },
            attract: {
                distance: 200,
                enable: false,
                rotate: {
                    x: 3000,
                    y: 3000,
                },
            },
            center: {
                x: 50,
                y: 50,
                mode: "percent",
                radius: 0,
            },
            decay: 0,
            distance: {},
            direction: "none",
            drift: 0,
            enable: true,
            gravity: {
                acceleration: 9.81,
                enable: false,
                inverse: false,
                maxSpeed: 50,
            },
            path: {
                clamp: true,
                delay: {
                    value: 0,
                },
                enable: false,
                options: {},
            },
            outModes: {
                default: "out",
            },
            random: false,
            size: false,
            speed: {
                min: 0.1,
                max: 1,
            },
            spin: {
                acceleration: 0,
                enable: false,
            },
            straight: false,
            trail: {
                enable: false,
                length: 10,
                fill: {},
            },
            vibrate: false,
            warp: false,
        },
        number: {
            density: {
                enable: true,
                width: 400,
                height: 400,
            },
            limit: {
                mode: "delete",
                value: 0,
            },
            value: 120,
        },
        reduceDuplicates: false,
        shape: {
            close: true,
            fill: true,
            options: {},
            type: "circle",
        },
        size: {
            value: {
                min: 2,
                max: 3,
            },
            animation: {
                count: 0,
                enable: false,
                speed: 5,
                decay: 0,
                delay: 0,
                sync: false,
                mode: "auto",
                startValue: "random",
                destroy: "none",
            },
        },
    },
    polygon: {
        draw: { enable: false, lineColor: "#ffffff", lineWidth: 0.5 },
        move: { radius: 10 },
        scale: 1,
        type: "none",
        url: "",
    },
    detectRetina: true,
};
